// @flow
import React, { type Node } from 'react';
import { hot } from 'react-hot-loader';
import Helmet from 'react-helmet-async';
import { connect } from 'react-redux';
import { compose, pure } from 'recompose';
import InfiniteScroll from 'react-infinite-scroller';
import { get, result } from 'lodash';
import classNames from 'classnames';
import { legacyAnalytics, analyticsActions, LegacyTrack } from 'angieslist-app-platform';
import urlCatalog from '@angi/url-catalog';
import { Alert, Col, Grid, Row } from 'angieslist-react-components';
import type { RemoveImagesAction } from '../../../app/common/images/imagesActions';
import { defaultProps } from '../../../app/browser/analyticsDefaultProperties';
import type { ImagesReducerState } from '../common/imagesReducer';
import imagesActions from '../../../app/common/images/imagesActions';
import type { ServiceProviderImage } from '../../../app/common/images/imagesService';
import EditMultipleImagesModal from '../../showcase/browser/components/EditMultipleImagesModal';
import imageManagementViewAnalytics from './imageManagementViewAnalytics';
import ImageRemoveModal from './components/ImageRemoveModal';
import ImageIndexCard from './components/ImageIndexCard';
import './imageManagementView.scss';
import type { ServiceProviderCacheState } from '../../../app/common/serviceProvider/serviceProviderReducer';
import CameraIcon from '../../profile/browser/assets/Camera';
import { getServiceProviderUpgradeState, PENDING_UPGRADE_STATE } from '../../home/common/selectors/upgradeStateSelectors';
import type { UpgradeStateType } from '../../home/common/selectors/upgradeStateSelectors';

const styles = {
  imageBox: {
    width: 150,
    height: 150,
    position: 'relative',
    display: 'block',
    margin: '1em auto 0 auto',
  },
};

// The limit is a bit arbitrary but you can see about 50 images at a time
// so it should be a little more than that
const limit = 50;

type Props = {
  // router
  params: {
    serviceProviderId: string
  },
  // Redux actions
  getPhotos: typeof imagesActions.getPhotos,
  addImages: typeof imagesActions.addImages,
  resetAddImages: typeof imagesActions.resetAddImages,
  updateImage: typeof imagesActions.updateImage,
  resetUpdateImage: typeof imagesActions.resetUpdateImage,
  removeImages: typeof imagesActions.removeImages,
  showConfirmRemoveImage: typeof imagesActions.showConfirmRemoveImage,
  hideConfirmRemoveImage: typeof imagesActions.hideConfirmRemoveImage,
  sendEnhancedTrackEvent: typeof analyticsActions.sendEnhancedTrackEvent,
  // Redux store
  images: ImagesReducerState,
  serviceProviderCache: ServiceProviderCacheState,
  browser: any,
  config: Object,
  serviceProviderUpgradeState: UpgradeStateType,
};

type State = {
  imageUploadValidationError: boolean | string,
};

const imageColProps = {
  xs: 6,
  sm: 4,
  md: 3,
  lg: 2,
};

const loader = ((
  <Col
    {...imageColProps}
    // loader will get appended to rendered list of images so it needs a unique key
    key="loader"
  >
    <div
      style={{
        height: styles.imageBox.height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <i className="icon icon-load icon-spin icon-5x text-primary" />
    </div>
  </Col>
));

const allowedImageTypes = new Set(['image/gif', 'image/jpeg', 'image/png']);
// eslint-disable-next-line no-bitwise
const maxSize = 10 << 20;

class ImageManagementViewComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      imageUploadValidationError: false,
    };
  }
  state: State

  componentDidMount() {
    const { uuid } =
      get(this.props.serviceProviderCache, this.props.params.serviceProviderId, '');
    if (uuid) {
      this.loadMoreImages(1);
    }
  }

  fileInputRef: any
  formRef: any

  loadMoreImages: ((page: number) => {|
    payload: {|
      data: {|limit: number, offset: number, uuid: string|},
      promise: any,
    |},
    type: string,
  |}) = (page: number) => {
    const { getPhotos } = this.props;
    const { uuid } =
      get(this.props.serviceProviderCache, this.props.params.serviceProviderId, '');
    return getPhotos({
      uuid,
      limit,
      offset: (page - 1) * limit,
    });
  }
  handleRemoveImage: ((image: ServiceProviderImage) => RemoveImagesAction) =
    (image: ServiceProviderImage) => {
      const { params: { serviceProviderId }, removeImages } = this.props;
      const { uuid } =
        get(this.props.serviceProviderCache, serviceProviderId, '');
      return removeImages({
        uuid,
        serviceProviderId,
        imageIds: [image.id],
      });
    }
  hasMoreImages: (() => boolean) = () => {
    const { images: { getPhotos } } = this.props;
    if (getPhotos.response === null || getPhotos.error || getPhotos.isLoading) {
      // Prevent fetching more images initially or when in error or loading states
      return false;
    }
    // Check the results length since the endpoint does not return a PaginationResult
    const requestImagesLength = get(getPhotos.response, 'images.length', -1);
    return requestImagesLength === -1 || requestImagesLength === limit;
  }
  dismissUploadError: (() => void) = () => {
    this.setState(() => ({ imageUploadValidationError: false }));
    this.props.resetAddImages();
  }

  updateImage: ((refetchShowcase: any, formValues: Object) => any)
    = async (refetchShowcase, formValues) => {
      const { serviceProviderId } = this.props.params;
      const { uuid } =
        get(this.props.serviceProviderCache, serviceProviderId, '');
      const imageInfo = {
        uuid,
        serviceProviderId,
        imageId: formValues.mediaId,
        caption: get(formValues, `imageDescriptionField_${formValues.mediaId}`),
      };
      return this.props.updateImage(imageInfo);
    };

  uploadImages: ((event: Event) => void) = (event: Event) => {
    this.dismissUploadError();
    const { serviceProviderId } = this.props.params;
    const { uuid } =
      get(this.props.serviceProviderCache, serviceProviderId, '');

    const fileList = get(event, 'target.files', []);
    const files = [];
    for (let i = 0; i < fileList.length; i += 1) {
      files.push(fileList[i]);
    }
    result(this.formRef, 'reset');
    for (let current = 0; current < files.length; current += 1) {
      const f = files[current];
      const allowedType = allowedImageTypes.has(f.type);
      const allowedSize = f.size <= maxSize;
      if (!allowedType) {
        this.setState(() => ({ imageUploadValidationError: `File "${f.name}" is not of an accepted filetype` }));
        this.props.sendEnhancedTrackEvent({
          name: 'Error Fired',
          properties: {
            ...defaultProps,
            serviceProviderId,
            description: 'Wrong File Type',
            fileType: f.type,
          },
        });
        return;
      } else if (!allowedSize) {
        this.setState(() => ({ imageUploadValidationError: `File "${f.name}" is too large` }));
        this.props.sendEnhancedTrackEvent({
          name: 'Error Fired',
          properties: {
            ...defaultProps,
            serviceProviderId,
            description: 'File Too Large',
            fileSize: f.size,
          },
        });
        return;
      }
    }
    this.props.addImages({
      uuid,
      serviceProviderId,
      files,
    });
  }
  props: Props

  render(): Node {
    const {
      resetUpdateImage,
      hideConfirmRemoveImage,
      showConfirmRemoveImage,
      images: {
        all, getPhotos, addRequest, updateImage, removeImages, showConfirmRemove,
      },
      browser,
      params: {
        serviceProviderId,
      },
      serviceProviderUpgradeState,
    } = this.props;

    const removingImageId = removeImages.isLoading && get(removeImages, 'request.imageIds[0]');
    const items = all.map(x => (
      <Choose key={x.id}>
        <When condition={removingImageId === x.id}>
          {loader}
        </When>
        <Otherwise>
          <Col key={x.id}>
            <ImageIndexCard
              image={x}
              onRemove={showConfirmRemoveImage}
              updateImage={this.updateImage}
              updateImageReset={resetUpdateImage}
              updateImageError={updateImage.error}
              updateImageLoading={updateImage.isLoading}
            />
          </Col>
        </Otherwise>
      </Choose>
    ));
    let imageRemoveModal;
    if (showConfirmRemove) {
      imageRemoveModal = (
        <ImageRemoveModal
          image={showConfirmRemove}
          show={!!showConfirmRemove}
          isLoading={removeImages.isLoading}
          browser={browser}
          onRemove={this.handleRemoveImage}
          onRequestHide={hideConfirmRemoveImage}
        />
      );
    }
    const pending = serviceProviderUpgradeState === PENDING_UPGRADE_STATE;

    return (
      <div>
        <Grid>
          <Helmet>
            <title>Photos</title>
          </Helmet>
          <h1>Photos</h1>
          <p>
            Add photos of your team, as well as your completed projects, to your profile.
            Pros with photos get up to 13% more profile views from homeowners.
            Images must be PNG or JPEG files and under 10MB.
          </p>
          <If condition={this.state.imageUploadValidationError || addRequest.error}>
            <Alert bsStyle="danger" onDismiss={this.dismissUploadError}>
              {this.state.imageUploadValidationError || 'We\'re sorry. An error has occurred. Please try again.'}
            </Alert>
          </If>
          <form
            id="add-images-form"
            // eslint-disable-next-line no-return-assign
            ref={ref => this.formRef = ref}
          >
            <input
              id="add-images"
              // eslint-disable-next-line no-return-assign
              ref={ref => this.fileInputRef = ref}
              type="file"
              accept={Array.from(allowedImageTypes).join(', ')}
              onChange={this.uploadImages}
              style={{ display: 'none' }}
              multiple
            />
          </form>
          {/*
      Warning: there are some pitfalls with using InfiniteScroll.
      Tweaking property values can lead to a short circuit of multiple requests
      to fetch all images. Ensure that the same page of images
      is never requested twice and that hasMore includes
      logic to guard against simultaneous requests.
    */}
          <InfiniteScroll
            // Not 0 since we fetch the first page on componentDidMount
            pageStart={1}
            // fetch on componentDidMount
            initialLoad={false}
            loadMore={this.loadMoreImages}
            hasMore={this.hasMoreImages()}
          >
            <Row>
              <If condition={removeImages.error}>
                <Col xs={12}>
                  <Alert bsStyle="danger">
                    There was an error processing your request.
                    Please reload the page or try again later.
                  </Alert>
                </Col>
              </If>
              {imageRemoveModal}
              <Col id="add-img-container" {...imageColProps}>
                <LegacyTrack>
                  <div
                    id="add-images-btn"
                    className={pending ? 'hide-pointer' : 'show-pointer'}
                    onClick={() => !pending && result(this.fileInputRef, 'click')}
                    onKeyDown={() => !pending && result(this.fileInputRef, 'click')}
                    role="button"
                    tabIndex={0}
                  >
                    <div id="camera-icon">
                      <CameraIcon
                        className={classNames('icon', pending && 'icon-disabled')}
                      />
                    </div>
                    <p
                      id="add-photos-text"
                      className={pending ? 'disabled' : 'enabled'}
                    >
                      Add a photo
                    </p>
                  </div>
                </LegacyTrack>
              </Col>
              <Choose>
                <When condition={addRequest.isLoading}>
                  {loader}
                </When>
                <When condition={addRequest.response}>
                  <EditMultipleImagesModal
                    updateShowcaseMediaError={updateImage.error}
                    updateShowcaseMediaIsLoading={updateImage.isLoading}
                    imagesToEdit={addRequest.response}
                    updateShowcaseMedia={this.updateImage}
                    updateShowcaseMediaReset={resetUpdateImage}
                    saveMediaReset={() => {}}
                    rotationEnabled={false}
                  />
                </When>
              </Choose>
              {items}
              {/*
          Do not use InfiniteScroll loader prop since that will only show
          the loader when hasMoreImages() is true but we want to show
          loading during the entire async request
        */}
              <If condition={getPhotos.isLoading}>
                {loader}
              </If>
            </Row>
          </InfiniteScroll>
          <p style={{ margin: '30px 16px 40px 0px' }}>
            Want to upload an entire project? Head to
            <a
              id="Edit-Profile--MenuNav-Photo-Gallery"
              href={urlCatalog.office.spProfile.showcases(serviceProviderId)}
              style={{ color: '#02819E', fontWeight: 'bold' }}
            >
              &nbsp;project showcase.
            </a>
          </p>
        </Grid>
      </div>
    );
  }
}

export { ImageManagementViewComponent, limit };

export default (hot(module)(compose(
  legacyAnalytics({
    ruleset: imageManagementViewAnalytics,
  }),
  connect(
    state => ({
      images: state.images,
      browser: state.browser,
      serviceProviderCache: state.serviceProviderCache,
      config: state.config,
      serviceProviderUpgradeState: getServiceProviderUpgradeState(state),
    }),
    {
      ...imagesActions,
      ...analyticsActions,
    },
  ),
  pure,
)(ImageManagementViewComponent)): any);



// WEBPACK FOOTER //
// ./src/modules/store/browser/ImageManagementView.js